import type {
  ChangePasswordRequest,
  ChangePasswordResponse,
} from "../types/auth.type";
import type { IAuthRepository } from "../repositories/AuthRepository";
import { getErrorMessage } from "../utils/errorMessage";
import { assertPasswordComplexity } from "../utils/passwordValidation";

export interface IChangePasswordUseCase {
  execute(request: ChangePasswordRequest): Promise<ChangePasswordResponse>;
}

export class ChangePasswordUseCase implements IChangePasswordUseCase {
  constructor(private readonly authRepository: IAuthRepository) {}

  async execute(
    request: ChangePasswordRequest,
  ): Promise<ChangePasswordResponse> {
    // Validate input
    if (!request.currentPassword || !request.newPassword) {
      throw new Error("Current password and new password are required");
    }

    if (request.currentPassword === request.newPassword) {
      throw new Error("New password must be different from current password");
    }

    // Password strength validation
    assertPasswordComplexity(request.newPassword);

    try {
      // Call repository to perform password change
      const response = await this.authRepository.changePassword(request);

      return response;
    } catch (error: unknown) {
      throw new Error(
        getErrorMessage(
          error,
          "Failed to change password. Please check your current password and try again.",
        ),
      );
    }
  }
}

// Factory function to create use case with dependencies
export const createChangePasswordUseCase = (
  authRepository: IAuthRepository,
): ChangePasswordUseCase => {
  return new ChangePasswordUseCase(authRepository);
};
